import Vue, { CreateElement } from '@src/vue'
import { Component, Prop, Watch } from 'vue-property-decorator'

import Member from '@src/data/member'
import { Subtitle1 } from '@src/elements/typography/typography'
import { Button } from '@src/elements/button/button'
import { Checkbox } from '@src/elements/input/checkbox'
import { ContentBox } from '@src/elements/content-box/content-box'
import { Spacer } from '@src/elements/spacer/spacer'
import EventBus from '@src/lib/event_bus'
import { MemberService } from '@src/service/member'
import SessionManager from '@src/lib/session_manager'
import { doesFileExist } from '@src/util/functions'
import { TERMS_FALLBACK_LOCALE } from '@src/util/constants'

import './terms-conditions.sass'

@Component({
  computed: {
    member () {
      return this.$store.state.selectedMember
    },
    locale () {
      return this.$store.state.i18n.locale
    }
  }
})
export class TermsAndConditions extends Vue {

  @Prop({ required: false })
  private acceptCallback: () => void

  @Prop({ required: false })
  // a specific member id can be sent via prop; else, the selected member in the store will be handled
  private memberId: number

  private member: Member
  private locale: string

  private acceptedPrivacyPolicy: boolean = false
  private acceptedTerms: boolean = false
  private acceptedNewsletter: boolean = false

  private highlightAcceptedPrivacyPolicy: boolean = false
  private highlightAcceptedTerms: boolean = false

  private service: MemberService = new MemberService()

  private termsLocale = TERMS_FALLBACK_LOCALE

  @Watch('locale', { immediate: true })
  checkTermsExists () {
    this.termsLocale = doesFileExist(`/assets/legal-docs/privacy-terms_${this.locale.toLowerCase()}.html`) ? this.locale : TERMS_FALLBACK_LOCALE
  }

  printPrivacyPolicy () {
    window.open(`/assets/legal-docs/privacy-terms_${this.termsLocale.toLowerCase()}.pdf`)
  }

  printTermsAndConditions () {
    window.open(`/assets/legal-docs/terms-of-use_${this.termsLocale.toLowerCase()}.pdf`)
  }

  goToMembersList () {
    this.$router.push('/trainers/platform/members')
  }

  attemptAccept () {
    if (this.acceptedPrivacyPolicy && this.acceptedTerms) {
      return this.accept()
    } else {
      this.highlightAcceptedPrivacyPolicy = !this.acceptedPrivacyPolicy
      this.highlightAcceptedTerms = !this.acceptedTerms
      this.$store.dispatch('showToast', { message: this.$t('PAGE.MEMBERS.LEGAL.REQUIRED'), fadeOut: true })
      this.scroll()
    }
  }

  accept () {
    let member: any = { termsAcceptedAt: new Date().toISOString() }
    if (this.acceptedNewsletter) {
      member.newsletterSubscribedAt = new Date().toISOString()
    }

    return this.service.acceptTermsAndConditions(this.memberId || this.member.id, member)
      .then(() => {
        if (this.acceptCallback) {
          this.acceptCallback()
        } else {
          EventBus.trigger('reloadSelectedMember')
        }
      })
  }

  scroll () {
    const termsCard = this.$refs.termsCard as Vue
    const container = termsCard.$refs.contentBoxBody as HTMLElement
    container.scrollTop = container.scrollHeight
  }

  render (h: CreateElement) {

    return <ContentBox overflow='scrollable' class='terms-card' ref='termsCard'>

      <div slot='content' class='terms-wrapper'>
        <Subtitle1 class='title'>{this.$t('PAGE.MEMBERS.LEGAL.UPDATED_TERMS_TITLE')}</Subtitle1>
        <Subtitle1>{this.$t('PAGE.MEMBERS.LEGAL.UPDATED_TERMS')}</Subtitle1>

        <Spacer />
        <Spacer />

        <div class='legal-wrapper'>
          <div class='legal-content'>
            <div class='iframe-label'>{this.$t('PAGE.MEMBERS.LEGAL.PRIVACY')}</div>
            <iframe src={`/assets/legal-docs/privacy-terms_${this.termsLocale.toLowerCase()}.html`} />
          </div>

          <Button type='remove' click={this.printPrivacyPolicy} icon={'util/print'} label={this.$t('PRINT')} />

          <Spacer />
          <div class='legal-content'>
            <div class='iframe-label'>{this.$t('PAGE.MEMBERS.LEGAL.UPDATED_TERMS_TITLE')}</div>
            <iframe src={`/assets/legal-docs/terms-of-use_${this.termsLocale.toLowerCase()}.html`} />
          </div>

          <Button type='remove' click={this.printTermsAndConditions} icon={'util/print'} label={this.$t('PRINT')} />
        </div>

        <Spacer />
        <Checkbox value={this.acceptedPrivacyPolicy} change={() => this.acceptedPrivacyPolicy = !this.acceptedPrivacyPolicy}
                  text={this.$t('PAGE.MEMBERS.LEGAL.PRIVACY_AGREEMENT')} ref='privacy' class={this.highlightAcceptedPrivacyPolicy ? 'error' : ''} />
        <Spacer />
        <Checkbox value={this.acceptedTerms} change={() => this.acceptedTerms = !this.acceptedTerms}
                  text={this.$t('PAGE.MEMBERS.LEGAL.TERMS_AGREEMENT')} ref='terms' class={this.highlightAcceptedTerms ? 'error' : ''} />
        <Spacer />
        <Checkbox value={this.acceptedNewsletter} change={() => this.acceptedNewsletter = !this.acceptedNewsletter}
                  text={this.$t('PAGE.MEMBERS.LEGAL.NEWSLETTER_AGREEMENT')} ref='newsletter' />
      </div>

      <div slot='bottom'>
        {SessionManager.Instance.isManager() ? <Button type='outline' click={this.goToMembersList} label={this.$t('NOT_NOW')} ref='skip' /> : null}
        <Button type='elevated' click={this.attemptAccept} label={this.$t('ACCEPT')} ref='accept' />
      </div>

    </ContentBox>
  }
}
